import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { enums, SignalBool, User, tokenService, tokenTimer, TokenEventArgs, idleTimeout } from "../base";

export interface AuthContextType {
    user?: User;
    signIn?: (username: string, password: string, remember: boolean, captchaId?: string, captchaCode?: string) => Promise<SignalBool<string>>;
    autoSignIn?: () => Promise<SignalBool<string>>;
    signOut?: () => void;
    refreshUser?: () => void;
    loading?: boolean; // 使用loading在加载时显示读取中而不是从登录页突然跳转到首页（或者相反）
    pwdSignal?: string | null;
    clearPwdSignal?: () => void;
}

const AuthContext: React.Context<AuthContextType> = createContext({});
const useAuth = () => useContext(AuthContext);
const pwdStorageKey = "pwd-signal";

function AuthProvider(props: any) {
    const [user, setUser] = useState<User | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [pwdSignal, setPwdSignal] = useState<string | null>(localStorage.getItem(pwdStorageKey));

    const onAuthChanged = useCallback(function (e: TokenEventArgs) {
        setUser(e.user);
    }, []);

    const clearPwdSignal = useCallback(function () {
        localStorage.removeItem(pwdStorageKey);
        setPwdSignal(null);
    }, []);

    const applyPwdSignal = useCallback((res: SignalBool<string>) => {
        const signal = res.signals && res.signals[enums.signalTypes.changePassword];
        if (signal) {
            localStorage.setItem(pwdStorageKey, signal);    // 保存，以防止使用刷新跳过密码修改
            setPwdSignal(signal);
        } else if (res.succeeded) {
            clearPwdSignal();
        }
    }, [clearPwdSignal]);

    const signIn = useCallback(async (username, password, remember, captchaId?, captchaCode?) => {
        const res = await tokenService.signIn(username, password, remember, captchaId, captchaCode);
        applyPwdSignal(res);
        return res;
    }, [applyPwdSignal]);

    const autoSignIn = useCallback(async () => {
        const res = await tokenService.autoSignIn();
        applyPwdSignal(res);
        return res;
    }, [applyPwdSignal]);

    const signOut = useCallback(function () {
        tokenService.signOut();
        clearPwdSignal();
    }, [clearPwdSignal]);

    const refreshUser = useCallback(async function () {
        const data = await tokenService.getUser(true);
        setUser(data);
    }, []);

    useEffect(function () {
        tokenService.addHandler(onAuthChanged);
        return function () {
            tokenService.removeHandler(onAuthChanged);
        }
    }, [onAuthChanged]);

    useEffect(function () {
        (async function () {
            setLoading(true);

            await tokenService.loadSessionSettings();

            var user = await tokenService.getUser();

            // 未登录尝试自动登录
            if (!user) {
                const result = await tokenService.autoSignIn();
                if (result.succeeded) {
                    user = await tokenService.getUser();
                }
            } else {
                // 已有短票（刷新页面）：按记住我决定是否启空闲超时
                idleTimeout.syncForCurrentSession(() => tokenService.clearStorage());
            }

            setUser(user);

            setLoading(false);

            if (user) {
                tokenTimer.initTimer(tokenService.refreshToken.bind(tokenService));
            }
        })();
    }, []);

    return (
        <AuthContext.Provider value={{ user, signIn, autoSignIn, signOut, refreshUser, loading, pwdSignal, clearPwdSignal }} {...props} />
    );
}

export {
    AuthContext,
    AuthProvider,
    useAuth
}